import dayjs from 'dayjs'

/**
 * Format a timestamp for display in the Last Activity tooltip.
 * Displays the full timestamp for precision when users need the exact date and time.
 *
 * Display format: [Month] D, YYYY at h:mm AM/PM
 * Example: March 5, 2026 at 3:42 PM
 *
 * @param timestamp - Unix timestamp in milliseconds, or undefined
 * @returns Formatted string with full timestamp, or empty string if undefined
 */
export function formatLastActivityForTooltip(timestamp: number | undefined): string {
  if (timestamp === undefined) {
    return ''
  }

  const activityTime = dayjs(timestamp)
  return activityTime.format('MMMM D, YYYY [at] h:mm A')
}

/**
 * Format a timestamp for display in the Last Activity table cell.
 * Displays human-readable relative time for easy scanning and quick comprehension.
 *
 * Display variations:
 * - Just now (< 1 minute)
 * - X minutes ago (1-59 minutes)
 * - Today at [time] (same day)
 * - Yesterday at [time] (yesterday)
 * - Day name + time (within past 7 days)
 * - Month + day (older than 7 days, same year)
 * - Month + day + year (prior year)
 * - (blank) (when timestamp is undefined)
 */
export function formatLastActivityForCell(timestamp: number | undefined): string {
  if (timestamp === undefined) {
    return ''
  }

  const now = dayjs()
  const activityTime = dayjs(timestamp)
  const diffInMinutes = now.diff(activityTime, 'minute')
  const diffInDays = now.diff(activityTime, 'day')

  // Just now (< 1 minute)
  if (diffInMinutes < 1) {
    return 'Just now'
  }

  // X minutes ago (1-59 minutes)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`
  }

  // Today at X:XX PM (same day)
  if (now.isSame(activityTime, 'day')) {
    return `Today at ${activityTime.format('h:mm A')}`
  }

  // Yesterday at X:XX AM (yesterday)
  if (now.subtract(1, 'day').isSame(activityTime, 'day')) {
    return `Yesterday at ${activityTime.format('h:mm A')}`
  }

  // Day of week at X:XX AM (within last 7 days)
  if (diffInDays < 7) {
    return `${activityTime.format('dddd')} at ${activityTime.format('h:mm A')}`
  }

  // Older than 7 days - check if same year or prior year
  if (now.isSame(activityTime, 'year')) {
    // Same year: Month + day (e.g., "Jan 14")
    return activityTime.format('MMM D')
  } else {
    // Prior year: Month + day + year (e.g., "Jan 14, 2024")
    return activityTime.format('MMM D, YYYY')
  }
}
